import React, { useEffect } from 'react';
import { publicCoordinates } from '../utils/publicCoordinates';

type StructuredData = Record<string, unknown> | Array<Record<string, unknown>>;

interface SeoProps {
  title: string;
  description: string;
  canonicalPath?: string;
  image?: string;
  type?: 'website' | 'article';
  robots?: string;
  structuredData?: StructuredData;
}

const DEFAULT_ROBOTS = 'index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1';

const absoluteUrl = (value: string, origin = window.location.origin) => {
  if (/^https?:\/\//i.test(value)) return value;
  return `${origin}${value.startsWith('/') ? '' : '/'}${value}`;
};

const upsertMeta = (selector: string, attributes: Record<string, string>) => {
  const matches = Array.from(document.head.querySelectorAll<HTMLMetaElement>(selector));
  const element = matches[0] || document.createElement('meta');
  Object.entries(attributes).forEach(([key, value]) => element.setAttribute(key, value));
  element.setAttribute('data-seo-client', 'true');
  if (!element.parentNode) document.head.appendChild(element);
  matches.slice(1).forEach((duplicate) => duplicate.remove());
};

const removeAll = (selector: string) =>
  document.head.querySelectorAll(selector).forEach((element) => element.remove());

/** Mantem os metadados corretos depois que o React assume a pagina. */
const Seo: React.FC<SeoProps> = ({
  title,
  description,
  canonicalPath,
  image,
  type = 'website',
  robots = DEFAULT_ROBOTS,
  structuredData,
}) => {
  useEffect(() => {
    document.title = title;

    const path = canonicalPath || window.location.pathname;
    const serverCanonicalElement = document.head.querySelector<HTMLLinkElement>(
      'link[rel="canonical"][data-seo-server]'
    );
    const canonicalOrigin = serverCanonicalElement
      ? new URL(serverCanonicalElement.href).origin
      : window.location.origin;
    const canonical = absoluteUrl(path.split('?')[0], canonicalOrigin);
    const serverCanonical = serverCanonicalElement?.href;
    const hasServerStructuredData = Boolean(
      document.head.querySelector('script[type="application/ld+json"][data-seo-server]')
    );
    const preserveServerStructuredData = serverCanonical === canonical && hasServerStructuredData;
    upsertMeta('meta[name="description"]', { name: 'description', content: description });
    upsertMeta('meta[name="robots"]', { name: 'robots', content: robots });
    upsertMeta('meta[property="og:type"]', { property: 'og:type', content: type });
    upsertMeta('meta[property="og:title"]', { property: 'og:title', content: title });
    upsertMeta('meta[property="og:description"]', { property: 'og:description', content: description });
    upsertMeta('meta[property="og:url"]', { property: 'og:url', content: canonical });
    upsertMeta('meta[name="twitter:card"]', { name: 'twitter:card', content: 'summary_large_image' });
    upsertMeta('meta[name="twitter:title"]', { name: 'twitter:title', content: title });
    upsertMeta('meta[name="twitter:description"]', { name: 'twitter:description', content: description });

    const canonicalMatches = Array.from(document.head.querySelectorAll<HTMLLinkElement>('link[rel="canonical"]'));
    const canonicalElement = canonicalMatches[0] || document.createElement('link');
    canonicalElement.rel = 'canonical';
    canonicalElement.href = canonical;
    canonicalElement.setAttribute('data-seo-client', 'true');
    if (!canonicalElement.parentNode) document.head.appendChild(canonicalElement);
    canonicalMatches.slice(1).forEach((duplicate) => duplicate.remove());

    if (image) {
      const absoluteImage = absoluteUrl(image);
      upsertMeta('meta[property="og:image"]', { property: 'og:image', content: absoluteImage });
      upsertMeta('meta[name="twitter:image"]', { name: 'twitter:image', content: absoluteImage });
    } else {
      removeAll('meta[property="og:image"], meta[name="twitter:image"]');
    }

    // Na carga direta, o servidor possui dados mais completos e vinculados ao
    // banco. Preserve-os enquanto o canonical for o mesmo. Em uma navegacao
    // client-side, remova o grafo da rota anterior e monte o grafo da nova rota.
    if (!preserveServerStructuredData) {
      removeAll('script[type="application/ld+json"][data-seo-server], script[type="application/ld+json"][data-seo-client]');
    }
    if (!preserveServerStructuredData && structuredData) {
      const graph = Array.isArray(structuredData) ? structuredData : [structuredData];
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute('data-seo-client', 'true');
      script.text = JSON.stringify({ '@context': 'https://schema.org', '@graph': graph });
      document.head.appendChild(script);
    }
  }, [canonicalPath, description, image, robots, structuredData, title, type]);

  return null;
};

export const organizationSchema = (siteSettings: any) => {
  const companyName = siteSettings?.company_info?.name || siteSettings?.site_name || 'Imobiliária';
  const raw = siteSettings?.settings || siteSettings || {};
  const schema: Record<string, unknown> = {
    '@type': 'RealEstateAgent',
    '@id': `${window.location.origin}/#organization`,
    name: companyName,
    url: `${window.location.origin}/`,
  };
  if (raw.logo || siteSettings?.customization?.logo_url) {
    schema.logo = absoluteUrl(raw.logo || siteSettings.customization.logo_url);
  }
  if (raw.phone || siteSettings?.company_info?.phone) schema.telephone = raw.phone || siteSettings.company_info.phone;
  if (raw.email || siteSettings?.company_info?.email) schema.email = raw.email || siteSettings.company_info.email;
  const address = raw.contact_address_full || raw.address || siteSettings?.company_info?.address;
  if (address) {
    const fields = typeof address === 'string' ? { streetAddress: address } : {
      streetAddress: [address.street, address.number].filter(Boolean).join(' '),
      addressLocality: address.city,
      addressRegion: address.state,
      postalCode: address.zip_code || address.zipCode,
    };
    schema.address = {
      '@type': 'PostalAddress',
      ...Object.fromEntries(Object.entries(fields).filter(([, value]) => Boolean(value))),
      addressCountry: 'BR',
    };
  }
  const coordinates = publicCoordinates(raw.contact_map_latitude, raw.contact_map_longitude);
  if (coordinates) schema.geo = { '@type': 'GeoCoordinates', latitude: coordinates[0], longitude: coordinates[1] };
  return schema;
};

export default Seo;
